import { startTransition, useRef, type SubmitEvent } from 'react';
import { useFormStatus } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, FilledInput, FormControl, FormHelperText, InputAdornment, InputLabel, Link } from '@mui/material';
import { AlternateEmail, Person } from '@mui/icons-material';

import { PasswordChangePageTitle, PasswordChangeSubmitButton } from './PasswordChange.style';
import LanguageSelector from '@/components/common/LanguageSelector';
import { type State } from '@/types';
import { FrontPage } from './FrontPage';
import type { ResetPasswordFields } from './ResetPasswordPage';

function Submit({ invitedUser }: { invitedUser: string | undefined }) {
  const { pending } = useFormStatus();
  const { t } = useTranslation();

  return (
    <PasswordChangeSubmitButton
      type="submit"
      fullWidth
      variant="contained"
      disabled={pending}
      data-qa-element-id="resend_password_button"
    >
      {!invitedUser ? t('login.resetPassword.resendPassword') : t('login.resetPassword.requestPassword')}
    </PasswordChangeSubmitButton>
  );
}

function ResetPassword({
  state,
  formAction,
  returnLoginPage,
  isPhone,
  invitedUser,
}: {
  state: State<ResetPasswordFields>;
  formAction: (payload: FormData) => void;
  returnLoginPage: () => void;
  isPhone: boolean;
  invitedUser: string | undefined;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLFormElement | null>(null);

  function handleSubmit(event: SubmitEvent<HTMLFormElement> | undefined) {
    event?.preventDefault();
    const form = ref.current;
    if (!form) {
      return;
    }
    const formData = new FormData(form);
    startTransition(() => formAction(formData));
  }

  return (
    <>
      {!isPhone && (
        <div>
          <PasswordChangePageTitle variant="h3" data-qa-element-id="reset_password_view_title">
            {t('login.resetPassword.newPassword')}
          </PasswordChangePageTitle>
          {!invitedUser && (
            <>
              <span data-qa-element-id="provide_username_and_email_label">
                {t('login.resetPassword.giveUserAndEmail')}
              </span>
              <Alert
                sx={{ fontSize: '12px', alignItems: 'center', marginTop: '20px', padding: '0px 10px 0px 10px' }}
                severity="warning"
                data-qa-element-id="must_use_itrack_username_and_email_alert"
              >
                {t('login.resetPassword.notification')}
              </Alert>
            </>
          )}
        </div>
      )}

      <Box component="form" ref={ref} onSubmit={handleSubmit} noValidate>
        {!invitedUser && (
          <FormControl
            variant="filled"
            margin="normal"
            fullWidth
            required
            error={state.errors.email !== undefined}
            data-qa-element-id="email_field"
            sx={{
              backgroundColor: 'primary.main',
              borderRadius: 'var(--mui-shape-borderRadius)',
              '& .MuiFormHelperText-root': {
                backgroundColor: '#fff',
                mx: 0,
                px: '14px',
                borderEndEndRadius: '3px',
                borderEndStartRadius: '3px',
                boxShadow: '0 2px 3px rgba(0, 0, 0, 0.1)',
              },
              '& .Mui-error::before': {
                borderWidth: 'thick',
              },
            }}
          >
            <InputLabel
              sx={{ color: 'white', fontWeight: 'bold' }}
              htmlFor="email"
              style={{ color: 'white' }}
              shrink={true}
              data-qa-element-id="email_input_label"
            >
              {t('login.resetPassword.email')}
            </InputLabel>
            <FilledInput
              id="email"
              data-qa-element-id="email_input_field"
              name="email"
              type="email"
              sx={{ color: 'white' }}
              endAdornment={
                <InputAdornment position="end" sx={{ padding: '8px' }} data-qa-element-id="email_input_icon">
                  <AlternateEmail sx={{ color: 'white' }} fontSize="large" />
                </InputAdornment>
              }
            />

            <FormHelperText id="password-error-text" data-qa-element-id="email_error_text">
              {state.errors.email}
            </FormHelperText>
          </FormControl>
        )}

        <FormControl
          variant="filled"
          margin="normal"
          fullWidth
          required={!invitedUser}
          autoFocus
          error={state.errors.username !== undefined}
          data-qa-element-id="username_field"
          sx={{
            backgroundColor: 'primary.main',
            marginBottom: '21px',
            borderRadius: 'var(--mui-shape-borderRadius)',
            '& .MuiFormHelperText-root': {
              backgroundColor: '#fff',
              mx: 0,
              px: '14px',
              borderEndEndRadius: '3px',
              borderEndStartRadius: '3px',
              boxShadow: '0 2px 3px rgba(0, 0, 0, 0.1)',
            },
            '& .Mui-error::before': {
              borderWidth: 'thick',
            },
          }}
        >
          <InputLabel
            sx={{ color: 'white', fontWeight: 'bold' }}
            htmlFor="username"
            style={{ color: 'white' }}
            shrink={true}
            data-qa-element-id="username_input_label"
          >
            {t('login.username')}
          </InputLabel>
          <FilledInput
            id="username"
            data-qa-element-id="username_input_field"
            name="username"
            type="username"
            defaultValue={invitedUser ?? ''}
            readOnly={Boolean(invitedUser)}
            sx={{ color: 'white' }}
            endAdornment={
              <InputAdornment position="end" sx={{ padding: '8px' }} data-qa-element-id="username_input_icon">
                <Person sx={{ color: 'white' }} fontSize="large" />
              </InputAdornment>
            }
          />
          <FormHelperText id="username-error-text" data-qa-element-id="username_error_text">
            {state.errors.username}
          </FormHelperText>
        </FormControl>

        <Submit invitedUser={invitedUser} />

        {!invitedUser && (
          <Box className="login-page-secondary-actions">
            <LanguageSelector />
            <Box className="login-page-forgot-password login-page-forgot-password-inline">
              <Link
                component="button"
                type="button"
                onClick={() => returnLoginPage()}
                underline="none"
                data-qa-element-id="return_to_login_button"
              >
                {t('login.resetPassword.back')}
              </Link>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
}

export function ResetPasswordForm({
  state,
  formAction,
  returnLoginPage,
  isPhone,
  invitedUser,
}: {
  state: State<ResetPasswordFields>;
  formAction: (payload: FormData) => void;
  returnLoginPage: () => void;
  isPhone: boolean;
  invitedUser: string | undefined;
}) {
  return (
    <FrontPage isPhone={isPhone}>
      <ResetPassword
        state={state}
        formAction={formAction}
        returnLoginPage={returnLoginPage}
        isPhone={isPhone}
        invitedUser={invitedUser}
      />
    </FrontPage>
  );
}
